import React, { useEffect, useState, useRef } from 'react';
import styles from './RecentItems.module.scss';

// Helper function to get product handles from localStorage
// In a real application, you'd also have logic to add product handles to this list
// when users visit product pages.
const getRecentlyViewedHandles = () => {
  const viewedHandlesString = localStorage.getItem('recentlyViewedProductHandles');
  if (viewedHandlesString) {
    try {
      const handles = JSON.parse(viewedHandlesString);
      return Array.isArray(handles) ? handles : [];
    } catch (e) {
      console.error("Error parsing recentlyViewedProductHandles from localStorage:", e);
      return [];
    }
  }
  return [];
};

export default function RecentItems({ fullSize = true }) {
  const [recentProducts, setRecentProducts] = useState([]);
  const sliderRef = useRef(null);
  
  useEffect(() => {
    const viewedHandles = getRecentlyViewedHandles();

    if (viewedHandles.length > 0) {
      const fetchProductPromises = viewedHandles.map(handle =>
        fetch(`/products/${handle}.json`)
          .then(response => {
            if (!response.ok) {
              // If a product is not found (e.g., deleted), it will fail.
              // Silently ignore these errors for a better user experience.
              console.warn(`Failed to fetch recent product ${handle}: ${response.statusText}`);
              return null;
            }
            return response.json();
          })
          .then(data => data.product) // Shopify product endpoint wraps product in a 'product' key
          .catch(error => {
            console.error("Error fetching details for recent product:", handle, error);
            return null; // Return null for failed fetches
          })
      );

      Promise.all(fetchProductPromises)
        .then(productsData => {
          // Filter out nulls (failed fetches) and limit the number of products if necessary
          setRecentProducts(productsData.filter(p => p !== null));
        });
    }
  }, []); // Empty dependency array to run once on mount

  const scrollLeft = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  if (recentProducts.length === 0) {
    return null; // Don't render anything if there are no recent items
  }

  return (
    <div className={`${styles.recentItemsContainer} ${fullSize ? styles.fullSizeContainer : ''}`}>
      <div className={styles.recentItemsHeader}>
        <h2 className={styles.recentItemsTitle}>Recently Viewed</h2>
        <div className={styles.arrowButtons}>
          <button onClick={scrollLeft} className={`${styles.arrow} ${styles.arrowLeft}`} aria-label="Scroll left">
            &lt;
          </button>
          <button onClick={scrollRight} className={`${styles.arrow} ${styles.arrowRight}`} aria-label="Scroll right">
            &gt;
          </button>
        </div>
      </div>
      <div className={styles.sliderWrapper}>
        <div className={styles.sliderContainer} ref={sliderRef}>
          {recentProducts.map((product) => {
            if (!product || !product.variants || !product.variants[0]) {
              return null; // Skip rendering if product data is incomplete
            }
            // Calculate discount percentage
            const price = parseFloat(product.variants[0].price);
            const compareAtPrice = parseFloat(product.variants[0].compare_at_price);
            let discount = 0;
            if (compareAtPrice && compareAtPrice > price) {
              discount = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
            }

            return (
              !product.tags.includes("BOLD_HIDDEN_PRODUCT") &&
              !product.tags.includes("OPTIONS_HIDDEN_PRODUCT") ? (
                <a href={`/products/${product.handle}`} key={product.id} className={styles.productLink}>
                  <div className={styles.productCard}>
                    <div className={styles.productImageContainer}>
                        <img src={product.image.src} alt={product.title} className={styles.productImage} />
                      {discount > 0 && (
                        <span className={styles.discountBadge}>{discount}% OFF</span>
                      )}
                    </div>
                    <h3 className={styles.productName}>{product.title}</h3>
                    <div className={styles.priceContainer}>
                      <span className={styles.currentPrice}>${price.toFixed(2)}</span>
                      {compareAtPrice && compareAtPrice > price && (
                        <span className={styles.originalPrice}>${compareAtPrice.toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                </a>
              ) : null
            );
          })}
        </div>
      </div>
    </div>
  );
}
